import { Link } from "react-router-dom";
import { ArrowRight, Cpu, Database, Brain, Sparkles, ChevronDown, Check, X } from "lucide-react";
import Layout from "@/components/Layout";
import Section from "@/components/Section";
import ServiceFlow from "@/components/ServiceFlow";
import HeroBackground from "@/components/HeroBackground";
import TrustTicker from "@/components/TrustTicker";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";
import { useTranslation } from "@/i18n/useTranslation";
import { usePageSeo } from "@/seo/usePageSeo";
import { faqPageJsonLd } from "@/seo/jsonLd";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Index = () => {
  const { t, locale } = useTranslation();
  const isFr = locale === "fr";
  const founderTags = locale === "fr"
    ? ["Systèmes IA", "Ingénierie de données", "ML / DS", "Systèmes décisionnels", "Mesure de rentabilité"]
    : ["AI systems", "Data engineering", "ML / DS", "Decision systems", "Profitability measurement"];

  const domainIndices = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const domains = domainIndices.map((i) => ({
    label: t(`home.domain${i}Label`),
    desc: t(`home.domain${i}Desc`),
  }));

  const faqs = Array.from({ length: 6 }, (_, i) => ({
    q: t(`home.faq${i + 1}Q`),
    a: t(`home.faq${i + 1}A`),
  }));

  const seo = usePageSeo("home");
  const faqJsonLd = faqPageJsonLd(faqs);

  const heroFlowNodes = isFr
    ? [
        { label: "Problème", sub: "Cadrage" },
        { label: "Conception", sub: "Système" },
        { label: "Construction", sub: "Livraison" },
        { label: "Opération", sub: "Production", emphasis: true },
      ]
    : [
        { label: "Problem", sub: "Frame" },
        { label: "Design", sub: "System" },
        { label: "Build", sub: "Deliver" },
        { label: "Operate", sub: "Production", emphasis: true },
      ];

  const offers = [
    { num: "01", icon: Cpu, title: t("home.offer1Title"), desc: t("home.offer1Desc"), dur: isFr ? "4–14 sem." : "4–14 weeks", href: "/services/agentic-ai" },
    { num: "02", icon: Sparkles, title: t("home.offer2Title"), desc: t("home.offer2Desc"), dur: isFr ? "4–12 sem." : "4–12 weeks", href: "/services/generative-ai" },
    { num: "03", icon: Brain, title: t("home.offer3Title"), desc: t("home.offer3Desc"), dur: isFr ? "4–14 sem." : "4–14 weeks", href: "/services/predictive-ai" },
    { num: "04", icon: Database, title: t("home.offer4Title"), desc: t("home.offer4Desc"), dur: isFr ? "4–14 sem." : "4–14 weeks", href: "/services/data-platforms" },
  ];

  const domainHrefs: (string | null)[] = [
    "/solutions/ai-agents-workflow-automation",
    "/solutions/customer-support-service-systems",
    "/solutions/knowledge-systems-enterprise-retrieval",
    "/solutions/document-intelligence",
    "/solutions/predictive-models-decision-science",
    null,
    null,
    "/solutions/data-platforms-for-ai",
    null,
  ];

  const processSteps = isFr
    ? [
        { title: "Définir le problème d'affaires", desc: "Quelle décision, quel processus ou quel livrable doit s'améliorer, et pourquoi ça compte financièrement." },
        { title: "Cartographier le processus, les données et la décision", desc: "Où le travail se fait, où résident les données, où se prend la décision, qui en est responsable." },
        { title: "Concevoir le système", desc: "Architecture, intégrations, contrôles, points de revue humaine, évaluation et chemin vers la production." },
        { title: "Construire la couche de données et d'IA", desc: "Référentiel unique, logique sémantique, modèles, agents, recherche — ce que le problème exige vraiment, rien de plus.", human: true },
        { title: "Déployer dans le processus", desc: "Mise en production, accès, surveillance, documentation et transfert à l'équipe qui l'exploitera." },
        { title: "Mesurer, surveiller, améliorer", desc: "Suivre l'adoption, la précision et l'impact d'affaires. Corriger ce qui traîne. Étendre ce qui fonctionne." },
      ]
    : [
        { title: "Define the business problem", desc: "What decision, workflow, or output needs to improve, and why it matters financially." },
        { title: "Map the workflow, data, and decision", desc: "Where work happens today, where data lives, where the decision is made, who owns it." },
        { title: "Design the system", desc: "Architecture, integrations, controls, human checkpoints, evaluation, and the path to production." },
        { title: "Build the data and AI layer", desc: "Source-of-truth, semantic logic, models, agents, retrieval — whatever the problem actually requires.", human: true },
        { title: "Deploy into the workflow", desc: "Production rollout, access, monitoring, documentation, and handoff to the team that will operate it." },
        { title: "Measure, monitor, improve", desc: "Track adoption, accuracy, and business outcome. Tune what underperforms. Expand what works." },
      ];

  const fitGood = isFr
    ? [
        "Un processus coûteux, lent, incohérent ou difficile à mettre à l'échelle.",
        "Des données qui existent, mais qui ne guident pas assez de décisions.",
        "Des tableaux de bord que les dirigeants débattent encore.",
        "Un besoin de prévision, de cotation, de routage, de mesure ou d'aide à la décision.",
        "Des outils IA achetés, mais qui ne s'intègrent pas au processus.",
        "Un système que des humains devront utiliser en production, avec confiance.",
      ]
    : [
        "A workflow that is expensive, slow, inconsistent, or hard to scale.",
        "Data that exists but does not drive enough decisions.",
        "Dashboards leadership still debates instead of acting on.",
        "A need for forecasting, scoring, routing, measurement, or decision support.",
        "AI tools you bought but they do not fit the workflow.",
        "A system people need to trust in production, not just demo.",
      ];

  const fitBad = isFr
    ? [
        "Vous cherchez de l'inspiration IA, sans problème précis à régler.",
        "Vous voulez un robot conversationnel générique, sans processus défini.",
        "Vous voulez une présentation stratégique, sans plan de mise en œuvre.",
        "Vous n'êtes pas prêt à exposer le vrai problème d'affaires.",
      ]
    : [
        "You want AI inspiration with no committed problem.",
        "You want a generic chatbot with no defined workflow.",
        "You want a strategy deck with no implementation path.",
        "You are not ready to expose the real business problem.",
      ];

  const guides = [
    { title: isFr ? "Comment évaluer un fournisseur IA" : "How to Vet an AI Vendor", desc: isFr ? "Démêler la vraie capacité du marketing soigné." : "Separate real capability from polished marketing.", href: "/insights/vet-ai-vendor" },
    { title: isFr ? "Ce que veut dire « IA en production »" : "What 'Production-Grade AI' Means", desc: isFr ? "Ce qui sépare une démo d'un système qui tient la route." : "What separates a demo from a reliable system.", href: "/insights/production-grade-ai" },
    { title: isFr ? "Quels processus méritent l'IA" : "Which Workflows Deserve AI", desc: isFr ? "Où l'IA crée de la valeur, et où elle n'en crée pas." : "Where AI creates value and where it doesn't.", href: "/insights/workflows-deserve-ai" },
  ];

  return (
    <Layout>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        jsonLd={faqJsonLd as unknown as Record<string, unknown>}
      />
      {/* HERO — 7/5 blueprint split */}
      <section className="relative min-h-[88vh] section-dark grain overflow-hidden flex items-center">
        <HeroBackground />
        <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-primary/35" />
        <div className="container-wide pt-28 pb-20 md:pt-36 md:pb-28 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-[7fr_5fr] gap-12 lg:gap-20 items-center">
            <div className="min-w-0 max-w-2xl">
              <p className="label-sm mb-7 !text-primary/55 opacity-0 animate-fade-up stagger-1">{t("home.heroLabel")}</p>
              <div className="w-10 h-px bg-primary/25 mb-10 opacity-0 animate-fade-up stagger-1 animate-breathe" />
              <h1 className="heading-hero text-[hsl(var(--sd-fg))] mb-10 opacity-0 animate-fade-up stagger-2">
                {t("home.heroTitle")}
              </h1>
              <p className="text-lead text-[hsl(var(--sd-fg)/0.42)] max-w-md mb-12 opacity-0 animate-fade-up stagger-3 font-light whitespace-pre-line">
                {t("home.heroSubtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 opacity-0 animate-fade-up stagger-4">
                <a href="https://calendly.com/d/cvpp-rwv-v5j" target="_blank" rel="noopener noreferrer" className="btn-primary group">
                  {t("nav.talkToUs")}
                  <ArrowRight className="w-3 h-3" />
                </a>
                <Link to="/services" className="btn-outline">
                  {t("nav.seeWhatWeBuild")}
                </Link>
              </div>
              <p className="font-mono text-[10.5px] text-[hsl(var(--sd-fg)/0.22)] tracking-[0.12em] uppercase mt-10 opacity-0 animate-fade-up stagger-5">
                {isFr
                  ? "4 mandats · 9 solutions · Livrables garantis"
                  : "4 engagements · 9 solutions · Guaranteed outputs"}
              </p>
            </div>

            <div className="relative opacity-0 animate-fade-up stagger-5 text-[hsl(var(--sd-fg))] max-w-md mx-auto lg:max-w-none w-full">
              <ServiceFlow nodes={heroFlowNodes} stamp={isFr ? "SYSTÈME / 01" : "SYSTEM / 01"} />
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
          <ChevronDown className="w-4 h-4 text-[hsl(var(--sd-fg)/0.18)] animate-scroll-hint" />
        </div>
      </section>

      <TrustTicker />

      {/* PROBLEM — indexed opener */}
      <Section narrow>
        <div className="flex items-baseline justify-between mb-6 gap-4">
          <p className="label-sm !mb-0">{isFr ? "Le problème" : "The problem"}</p>
          <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/25">
            P / 00
          </span>
        </div>
        <div className="border-t border-border/20 pt-12">
          <div className="grid grid-cols-[3rem_1fr] sm:grid-cols-[5rem_1fr] gap-x-5">
            <span className="font-mono text-[10px] tracking-[0.16em] text-primary/55 pt-2">P/00</span>
            <div className="min-w-0">
              <h2 className="heading-section text-foreground mb-8 leading-tight max-w-2xl">
                {isFr
                  ? "La plupart des projets IA s'éteignent avant de devenir des systèmes."
                  : "Most AI projects fail before they become systems."}
              </h2>
              <div className="space-y-6 max-w-2xl text-[16px] text-muted-foreground leading-[1.85] font-light">
                <p>
                  {isFr
                    ? "La démo est facile. La production, elle, exige des données fiables, une intégration aux processus, des permissions, une supervision humaine, de la gouvernance, de la surveillance et une économie claire. C'est là que la plupart des initiatives s'arrêtent en silence."
                    : "Demos are easy. Production requires trusted data, workflow fit, integrations, evaluation, permissions, human approval, governance, monitoring, adoption, and clear economics. That is where most initiatives quietly stall."}
                </p>
                <p className="text-foreground/85 font-normal">
                  {isFr
                    ? "Vert AI ne vend pas du potentiel. On livre une capacité d'exploitation."
                    : "Vert AI does not sell possibility. We build operating capability."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* DECISION SYSTEMS thesis — comparison panel on dark */}
      <Section fullBleed dark grain animate>
        <div className="container-wide section-padding relative z-10">
          <div className="flex items-baseline justify-between mb-6 gap-4">
            <p className="label-sm !text-primary/55 !mb-0">{isFr ? "Ce qu'on construit" : "What we build"}</p>
            <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--sd-fg)/0.25)]">
              D / 02
            </span>
          </div>
          <h2 className="heading-section text-[hsl(var(--sd-fg))] leading-tight max-w-3xl mb-14 md:mb-20">
            {isFr
              ? "Les tableaux de bord disent ce qui s'est passé. Les systèmes décisionnels disent quoi faire ensuite."
              : "Dashboards show what happened. Decision systems help the business decide what to do next."}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[hsl(var(--sd-fg)/0.08)] mb-14 border border-[hsl(var(--sd-fg)/0.08)]">
            {[
              {
                code: "D/01",
                kind: isFr ? "Tableau de bord" : "Dashboard",
                role: isFr ? "Décrit le passé" : "Describes the past",
                muted: true,
                rows: isFr
                  ? [
                      ["Sortie", "Graphiques, KPI, rapports"],
                      ["Consommé par", "Humains, en réunion"],
                      ["Question répondue", "Que s'est-il passé ?"],
                      ["Action", "Encore à décider"],
                      ["Mesure de succès", "Visites, exports"],
                    ]
                  : [
                      ["Output", "Charts, KPIs, reports"],
                      ["Consumed by", "Humans, in meetings"],
                      ["Question answered", "What happened?"],
                      ["Action", "Still up to a person"],
                      ["Success metric", "Views, exports"],
                    ],
              },
              {
                code: "D/02",
                kind: isFr ? "Système décisionnel" : "Decision system",
                role: isFr ? "Guide la prochaine action" : "Guides the next action",
                muted: false,
                rows: isFr
                  ? [
                      ["Sortie", "Décision cotée et traçable"],
                      ["Consommé par", "Le processus, en continu"],
                      ["Question répondue", "Que faire ensuite, et pourquoi"],
                      ["Action", "Routée, exécutée, mesurée"],
                      ["Mesure de succès", "Résultat d'affaires"],
                    ]
                  : [
                      ["Output", "A scored, traceable decision"],
                      ["Consumed by", "The workflow, continuously"],
                      ["Question answered", "What to do next, and why"],
                      ["Action", "Routed, executed, measured"],
                      ["Success metric", "Business outcome"],
                    ],
              },
            ].map((col) => (
              <div
                key={col.kind}
                className="bg-[hsl(var(--off-black))] p-7 sm:p-9 md:p-12 relative min-w-0"
              >
                <div className="flex items-start justify-between gap-4 mb-8 pb-6 border-b border-[hsl(var(--sd-fg)/0.07)]">
                  <div className="min-w-0">
                    <p
                      className={`font-mono text-[10px] tracking-[0.16em] uppercase mb-3 ${
                        col.muted ? "text-[hsl(var(--sd-fg)/0.32)]" : "text-primary/75"
                      }`}
                    >
                      {col.code} · {col.kind}
                    </p>
                    <h3
                      className={`text-[18px] font-medium tracking-[-0.015em] ${
                        col.muted ? "text-[hsl(var(--sd-fg)/0.45)]" : "text-[hsl(var(--sd-fg))]"
                      }`}
                    >
                      {col.role}
                    </h3>
                  </div>
                  {col.muted ? (
                    <X className="w-4 h-4 text-[hsl(var(--sd-fg)/0.18)] flex-shrink-0 mt-1" strokeWidth={1.5} />
                  ) : (
                    <Check className="w-4 h-4 text-primary/70 flex-shrink-0 mt-1" strokeWidth={1.5} />
                  )}
                </div>
                <dl className="divide-y divide-[hsl(var(--sd-fg)/0.05)]">
                  {col.rows.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[110px_1fr] gap-4 items-baseline py-4 first:pt-0">
                      <dt className="font-mono text-[10px] tracking-[0.14em] uppercase text-[hsl(var(--sd-fg)/0.30)] min-w-0">
                        {k}
                      </dt>
                      <dd
                        className={`text-[14.5px] leading-[1.55] min-w-0 ${
                          col.muted
                            ? "text-[hsl(var(--sd-fg)/0.42)] font-light"
                            : "text-[hsl(var(--sd-fg)/0.85)] font-normal"
                        }`}
                      >
                        {v}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-4">
            <span className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-primary/55 pt-1">HUMAN /</span>
            <p className="text-[hsl(var(--sd-fg)/0.78)] text-[16px] font-medium max-w-2xl leading-[1.65]">
              {isFr
                ? "Les meilleures décisions d'affaires ne viennent pas d'un tableau de bord plus joli. Elles viennent d'un système conçu pour les guider."
                : "Better commercial decisions do not come from a prettier dashboard. They come from a system designed to guide them."}
            </p>
          </div>
        </div>
      </Section>

      {/* DIFFERENTIATORS — sticky manifesto + indexed registry */}
      <Section fullBleed animate>
        <div className="container-wide section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <div className="flex items-baseline justify-between mb-6 gap-4">
                <p className="label-sm !mb-0">{t("home.whyUsLabel")}</p>
                <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/25">W / 04</span>
              </div>
              <h2 className="heading-section-lg text-foreground leading-tight mb-8 whitespace-pre-line">
                {t("home.whyUsTitle")}
              </h2>
              <p className="text-muted-foreground text-[15.5px] leading-[1.8] max-w-md font-light">
                {t("home.whyUsSubtitle")}
              </p>
              <div className="mt-10 w-12 h-px bg-primary/30" />
            </div>

            <ul className="lg:col-span-7 border-t border-border/20">
              {[
                { title: t("home.diff1Title"), desc: t("home.diff1Desc") },
                { title: t("home.diff2Title"), desc: t("home.diff2Desc") },
                { title: t("home.diff3Title"), desc: t("home.diff3Desc") },
                { title: t("home.diff4Title"), desc: t("home.diff4Desc") },
              ].map((item, i) => (
                <li
                  key={item.title}
                  className="grid grid-cols-[3rem_1fr] sm:grid-cols-[5rem_1fr] gap-x-5 py-8 md:py-10 border-b border-border/15 group hover:border-primary/30 transition-colors duration-400 border-l-2 border-l-transparent hover:border-l-primary/35 pl-3 -ml-3"
                >
                  <span className="font-mono text-[10px] tracking-[0.16em] text-foreground/30 pt-1 group-hover:text-primary/70 transition-colors duration-400">
                    W/{String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-[16px] font-semibold text-foreground mb-2.5 font-sans tracking-[-0.012em] group-hover:text-primary transition-colors duration-400">
                      {item.title}
                    </h3>
                    <p className="text-[14.5px] text-muted-foreground leading-[1.75] font-light max-w-xl">
                      {item.desc}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* CORE OFFERS — indexed registry (S/01…S/04) */}
      <Section>
        <div className="flex items-baseline justify-between mb-6 gap-4">
          <p className="label-sm !mb-0">{t("home.engagementsLabel")}</p>
          <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/25">S / 04 · {isFr ? "Registre" : "Registry"}</span>
        </div>
        <h2 className="heading-section text-foreground mb-4 max-w-lg leading-tight whitespace-pre-line">
          {t("home.engagementsTitle")}
        </h2>
        <p className="text-lead text-muted-foreground max-w-lg mb-14 font-light">
          {t("home.engagementsSubtitle")}
        </p>
        <ul className="border-t border-border/20">
          {offers.map((o) => {
            const Icon = o.icon;
            return (
              <li key={o.num} className="border-b border-border/15 group hover:border-primary/30 transition-colors duration-400">
                <Link
                  to={o.href}
                  className="grid grid-cols-[3rem_1fr] sm:grid-cols-[5rem_1fr_auto] gap-x-5 gap-y-2 py-7 md:py-10 items-start border-l-2 border-l-transparent group-hover:border-l-primary/35 pl-3 -ml-3 transition-colors duration-400"
                >
                  <span className="font-mono text-[10px] tracking-[0.16em] text-foreground/30 pt-1 group-hover:text-primary/70 transition-colors duration-400">
                    S/{o.num}
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5 mb-2">
                      <Icon className="w-3.5 h-3.5 text-primary/45" />
                      <h3 className="text-[15.5px] font-semibold text-foreground font-sans tracking-[-0.01em] group-hover:text-primary transition-colors duration-400">
                        {o.title}
                      </h3>
                      <span className="font-mono text-[9.5px] text-muted-foreground/45 tracking-[0.12em] uppercase">· {o.dur}</span>
                    </div>
                    <p className="text-[14px] text-muted-foreground/80 leading-[1.75] font-light max-w-xl">
                      {o.desc}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center mt-1 col-start-3">
                    <span className="font-mono text-[10px] text-primary/0 group-hover:text-primary/60 tracking-[0.12em] uppercase whitespace-nowrap transition-colors duration-400">
                      → {isFr ? "Démarrer" : "Start"}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </Section>

      {/* IMPLEMENTATION DOMAINS — hairline tile grid (So/01…So/09) */}
      <Section padding="sm">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <div className="flex items-baseline gap-4 mb-6">
              <p className="label-sm !mb-0">{t("home.domainsLabel")}</p>
              <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/25">So / 09</span>
            </div>
            <h2 className="heading-section text-foreground mb-5 leading-tight max-w-md whitespace-pre-line">
              {t("home.domainsTitle")}
            </h2>
            <p className="text-[15px] text-muted-foreground max-w-xl leading-[1.8] font-light">
              {t("home.domainsSubtitle")}
            </p>
          </div>
          <Link
            to="/solutions"
            className="link-underline font-mono text-[10.5px] font-medium tracking-[0.1em] uppercase text-foreground flex items-center gap-1.5 flex-shrink-0 transition-colors duration-400"
          >
            {isFr ? "Toutes les solutions" : "All solutions"} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/35 dark:bg-border/15 border border-border/35 dark:border-border/15">
          {domains.map((item, i) => {
            const href = domainHrefs[i];
            const inner = (
              <div className="h-full bg-background dark:bg-surface-1 p-6 md:p-7 group-hover:bg-primary/[0.02] transition-colors duration-400 min-w-0">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-[10px] tracking-[0.16em] text-primary/55 group-hover:text-primary/85 transition-colors duration-400">
                    So/{String(i + 1).padStart(2, "0")}
                  </span>
                  {href && (
                    <ArrowRight className="w-3 h-3 text-muted-foreground/25 group-hover:text-primary/70 group-hover:translate-x-0.5 transition-all duration-400" />
                  )}
                </div>
                <h3 className="text-[14.5px] font-semibold text-foreground font-sans mb-2.5 tracking-[-0.01em] group-hover:text-primary transition-colors duration-400 leading-snug">
                  {item.label}
                </h3>
                <p className="text-[13.5px] text-muted-foreground/75 leading-[1.7] font-light">{item.desc}</p>
              </div>
            );
            return href ? (
              <Link key={item.label} to={href} className="group block">{inner}</Link>
            ) : (
              <div key={item.label} className="group block">{inner}</div>
            );
          })}
        </div>
      </Section>

      {/* PROCESS — indexed stack (P/01…P/06) */}
      <Section>
        <div className="flex items-baseline justify-between mb-6 gap-4">
          <p className="label-sm !mb-0">{isFr ? "Notre façon de travailler" : "How we work"}</p>
          <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/25">P / 06</span>
        </div>
        <h2 className="heading-section text-foreground mb-5 leading-tight max-w-lg">
          {isFr ? "Du problème d'exploitation au système en production." : "From the operating problem to a system in production."}
        </h2>
        <p className="text-[15px] text-muted-foreground max-w-xl leading-[1.8] font-light mb-14">
          {isFr
            ? "On ne commence pas par un modèle. On commence par le problème d'exploitation, et on conçoit à rebours."
            : "We do not start with a model. We start with the operating problem and design backward from it."}
        </p>

        <ul className="border-t border-border/20 max-w-4xl">
          {processSteps.map((step, i) => (
            <li
              key={i}
              className="grid grid-cols-[3rem_1fr] sm:grid-cols-[5rem_1fr_auto] gap-x-5 py-7 md:py-9 border-b border-border/15 group hover:border-primary/30 transition-colors duration-400 border-l-2 border-l-transparent hover:border-l-primary/35 pl-3 -ml-3"
            >
              <span className="font-mono text-[10px] tracking-[0.16em] text-foreground/30 pt-1 group-hover:text-primary/70 transition-colors duration-400">
                P/{String(i + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0">
                <h3 className="text-[16px] font-semibold text-foreground mb-2 tracking-[-0.012em] group-hover:text-primary transition-colors duration-400">{step.title}</h3>
                <p className="text-[14.5px] text-muted-foreground/85 leading-[1.78] font-light max-w-xl">{step.desc}</p>
              </div>
              {step.human && (
                <div className="hidden sm:flex items-start pt-1.5 col-start-3">
                  <span className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-primary/55">HUMAN /</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Section>

      {/* FOUNDER — dark cinematic with stamp + monogram */}
      <Section fullBleed dark grain animate>
        <div className="container-wide section-padding relative z-10">
          <div className="absolute top-1/2 right-12 -translate-y-1/2 font-mono text-[12rem] md:text-[18rem] font-bold text-[hsl(var(--sd-fg)/0.012)] leading-none pointer-events-none select-none hidden lg:block">
            GB
          </div>

          <div className="flex items-baseline justify-between mb-12 md:mb-16 gap-4">
            <p className="label-sm !text-primary/55 !mb-0">{t("home.founderLabel")}</p>
            <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--sd-fg)/0.25)]">FOUNDER / 01</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 lg:gap-24 items-start relative">
            <div>
              <div className="w-px h-20 bg-primary/20 mb-6" />
              <p className="text-[15px] font-medium text-[hsl(var(--sd-fg)/0.55)]">{t("home.founderName")}</p>
              <p className="font-mono text-[10.5px] text-[hsl(var(--sd-fg)/0.25)] mt-1.5 tracking-[0.14em] uppercase">{t("home.founderRole")}</p>
            </div>
            <div>
              <h2 className="heading-section text-[hsl(var(--sd-fg)/0.78)] mb-10 leading-tight">
                {t("home.founderName")}
              </h2>
              <div className="space-y-6 text-[15px] text-[hsl(var(--sd-fg)/0.40)] leading-[1.8] max-w-lg font-light">
                <p>{t("home.founderP1")}</p>
                <p>{t("home.founderP2")}</p>
                <p className="text-[hsl(var(--sd-fg)/0.60)] font-medium">{t("home.founderP3")}</p>
              </div>
              <ul className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[hsl(var(--sd-fg)/0.05)] border border-[hsl(var(--sd-fg)/0.05)] max-w-xl">
                {founderTags.map((tag, i) => (
                  <li key={tag} className="bg-[hsl(var(--off-black))] px-4 py-3 flex items-center gap-3">
                    <span className="font-mono text-[9.5px] tracking-[0.16em] uppercase text-primary/55">K/{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[12.5px] text-[hsl(var(--sd-fg)/0.55)] font-light">{tag}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* FIT — comparison panel */}
      <Section padding="sm">
        <div className="flex items-baseline justify-between mb-6 gap-4">
          <p className="label-sm !mb-0">{isFr ? "Quand nous écrire" : "When to talk to us"}</p>
          <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/25">F / 02</span>
        </div>
        <h2 className="heading-section text-foreground mb-5 leading-tight max-w-2xl">
          {isFr
            ? "Quand le problème est assez concret pour qu'on puisse le bâtir."
            : "Talk to us when the problem is real enough to build around."}
        </h2>
        <p className="text-[15px] text-muted-foreground max-w-2xl leading-[1.8] font-light mb-12">
          {isFr
            ? "On qualifie tôt. C'est plus respectueux du temps de tout le monde."
            : "We qualify early. It is more respectful of everyone's time."}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border/35 dark:bg-border/15 border border-border/35 dark:border-border/15">
          {[
            {
              code: "F/01",
              label: isFr ? "Pour nous" : "Good fit",
              accent: true,
              items: fitGood,
            },
            {
              code: "F/02",
              label: isFr ? "Pas pour nous" : "Not a fit",
              accent: false,
              items: fitBad,
            },
          ].map((col) => (
            <div key={col.code} className="bg-background dark:bg-surface-1 p-8 md:p-10 min-w-0">
              <div className="flex items-center gap-2.5 mb-7 pb-5 border-b border-border/20">
                <span className={`w-1.5 h-1.5 rounded-full ${col.accent ? "bg-primary/70" : "bg-muted-foreground/40"}`} />
                <span className={`font-mono text-[10.5px] tracking-[0.16em] uppercase ${col.accent ? "text-primary/85" : "text-muted-foreground/65"}`}>
                  {col.code} · {col.label}
                </span>
              </div>
              <ul className="divide-y divide-border/15">
                {col.items.map((item, i) => (
                  <li key={item} className="grid grid-cols-[auto_1fr] gap-4 py-4 first:pt-0">
                    <span className="font-mono text-[10px] tracking-[0.14em] text-foreground/25 pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex items-start gap-2.5">
                      {col.accent ? (
                        <Check className="w-3.5 h-3.5 text-primary/60 mt-[3px] flex-shrink-0" />
                      ) : (
                        <X className="w-3.5 h-3.5 text-muted-foreground/35 mt-[3px] flex-shrink-0" />
                      )}
                      <span className={`text-[14px] leading-[1.7] font-light ${col.accent ? "text-muted-foreground" : "text-muted-foreground/65"}`}>{item}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* GUIDES — hairline tile grid (G/01…G/03) */}
      <Section padding="sm" className="ambient-glow">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-baseline gap-4 mb-4">
              <p className="label-sm !mb-0">{isFr ? "Guides de référence" : "Strategic guides"}</p>
              <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/25">G / 03</span>
            </div>
            <h2 className="heading-subsection text-foreground leading-tight">
              {isFr ? "Pour les dirigeants qui doivent décider." : "References for decision-makers."}
            </h2>
          </div>
          <Link to="/insights" className="link-underline font-mono text-[10.5px] font-medium tracking-[0.1em] uppercase text-foreground flex items-center gap-1.5 flex-shrink-0 transition-colors duration-400">
            {t("home.allInsights")} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border/35 dark:bg-border/15 border border-border/35 dark:border-border/15">
          {guides.map((guide, i) => (
            <Link
              key={guide.href}
              to={guide.href}
              className="group block bg-background dark:bg-surface-1 p-7 md:p-8 hover:bg-primary/[0.02] transition-colors duration-400 min-w-0"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-mono text-[10px] tracking-[0.16em] text-primary/55 group-hover:text-primary/85 transition-colors duration-400">
                  G/{String(i + 1).padStart(2, "0")}
                </span>
                <ArrowRight className="w-3 h-3 text-muted-foreground/25 group-hover:text-primary/70 group-hover:translate-x-0.5 transition-all duration-400" />
              </div>
              <h3 className="text-[15px] text-foreground font-semibold tracking-[-0.01em] group-hover:text-primary transition-colors duration-400 leading-snug mb-2.5">
                {guide.title}
              </h3>
              <p className="text-[13px] text-muted-foreground/70 font-light leading-[1.7]">
                {guide.desc}
              </p>
            </Link>
          ))}
        </div>
      </Section>

      {/* FAQ — indexed accordion */}
      <Section narrow>
        <div className="flex items-baseline justify-between mb-6 gap-4">
          <p className="label-sm !mb-0">{t("home.faqLabel")}</p>
          <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-foreground/25">FAQ / {String(faqs.length).padStart(2, "0")}</span>
        </div>
        <h2 className="heading-section text-foreground mb-14 leading-tight">
          {t("home.faqTitle")}
        </h2>
        <Accordion type="single" collapsible className="w-full border-t border-border/20">
          {faqs.map((item, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border/15">
              <AccordionTrigger className="text-left text-foreground font-sans font-medium text-[15px] py-7 px-1 hover:no-underline hover:text-primary transition-colors duration-500 group">
                <span className="flex items-baseline gap-5 flex-1 min-w-0">
                  <span className="font-mono text-[10px] tracking-[0.16em] text-foreground/30 group-hover:text-primary/70 transition-colors duration-400 flex-shrink-0">
                    FAQ/{String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="min-w-0">{item.q}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-[1.8] text-[15px] pb-7 pl-[calc(10px+4ch+1.25rem)] pr-1 font-light">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* END cap */}
      <Section narrow className="!pt-0 !pb-10">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-foreground/25">END /</span>
          <span className="h-px w-12 bg-foreground/15" />
        </div>
      </Section>

      <CTABanner variant="dark" headline={t("home.ctaHeadline")} description={t("home.ctaDescription")} />
    </Layout>
  );
};

export default Index;
